import React, { useState, useEffect } from 'react';
import CommonModal from '../../../extras/commonModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Checkbox,
  ListItemText,
  Chip,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import './studentGroupModal.css';
import { deleteStudentGroup, updateStudentGroup } from '../../../services/apiService';
import { toast } from 'react-toastify';

const initialFormState = {
  groupName: '',
  groupDescription: '',
  groupType: '',
  staticOption: '', // 'userIds' or 'emails'
  userIdList: '',
  emailList: '',
  dynamicOption: '', // 'program', 'cohort', 'almacohort', 'daterange'
  // Program multi-selects
  programCourseList: [],
  programPlanList: [],
  programSubplanList: [],
  // Cohort multi-selects
  selectedCohorts: [],
  dateRange: '', // Combined date range display
  startDate: null, // For DatePicker
  endDate: null, // For DatePicker
};

const StudentGroupEditModal = ({
  openEditModal,
  onRefresh,
  setOpenEditModal,
  rowData,
  cohortListProp = [],
  almaxCohortListProp = [],
  programOptionsProp = { course: [], plan: [], subplan: [] },
}) => {
  const [formState, setFormState] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [cohortList, setCohortList] = useState([]);
  const [almaxCohortList, setAlmaxCohortList] = useState([]);
  const [programOptions, setProgramOptions] = useState({
    course: [],
    plan: [],
    subplan: [],
  });

  useEffect(() => {
    if (!openEditModal) return;
    setCohortList(Array.isArray(cohortListProp) ? cohortListProp : []);
    setAlmaxCohortList(
      Array.isArray(almaxCohortListProp) ? almaxCohortListProp : []
    );
    setProgramOptions(
      programOptionsProp && typeof programOptionsProp === 'object'
        ? programOptionsProp
        : { course: [], plan: [], subplan: [] }
    );
  }, [openEditModal, cohortListProp, almaxCohortListProp, programOptionsProp]);

  useEffect(() => {
    if (openEditModal && rowData) {
      if (formState.groupName !== rowData.groupName) {
        const groupTypeParts = rowData.groupType?.split('-') || [];
        const mainGroupType = groupTypeParts[0] || '';
        const subType = groupTypeParts[1] || '';

        const params = rowData.params || {};

        // Prefill for enrolmentDate type
        let startDate = null;
        let endDate = null;
        let dynamicOptionValue = subType || '';
        if (params.type === 'enrolmentDate' && params.enrolmentDate) {
          startDate = params.enrolmentDate.startDate ? new Date(params.enrolmentDate.startDate) : null;
          endDate = params.enrolmentDate.endDate ? new Date(params.enrolmentDate.endDate) : null;
          dynamicOptionValue = 'daterange';
        } else {
          startDate = params.startDate ? new Date(params.startDate) : null;
          endDate = params.endDate ? new Date(params.endDate) : null;
        }
        const dateRange = startDate && endDate ? formatDateRange(startDate, endDate) : '';

        setFormState({
          groupName: rowData.groupName || '',
          groupDescription: rowData.groupDecr || '',
          groupType: mainGroupType,
          staticOption:
            rowData?.params?.type === 'userId'
              ? 'userIds'
              : rowData?.params?.type === 'emailId'
              ? 'emails'
              : '',
          userIdList: '',
          emailList: '',
          dynamicOption: dynamicOptionValue,
          programCourseList: params.program?.course || [],
          programPlanList: params.program?.plan || [],
          programSubplanList: params.program?.subplan || [],
          selectedCohorts: params.cohort || params.almaxCohort || [],
          dateRange: dateRange,
          startDate: startDate,
          endDate: endDate,
        });
      }
    }
  }, [openEditModal, rowData]);

  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFormState((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
      dateRange: start && end ? formatDateRange(start, end) : '',
    }));
  };

  const handleClose = () => {
    setOpenEditModal(false);
    setFormState(initialFormState); // Reset form on close
    setIsLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGroupTypeChange = (value) => {
    // Reset relevant fields when group type changes
    setFormState((prev) => ({
      ...prev,
      groupType: value,
      staticOption: '',
      userIdList: '',
      emailList: '',
      dynamicOption: '',
      // program selections
      programCourseList: [],
      programPlanList: [],
      programSubplanList: [],
      // cohort selections
      selectedCohorts: [],
      dateRange: '',
      startDate: null,
      endDate: null,
    }));
  };
  const handleStaticOptionChange = (value) => {
    // Reset input fields when static option changes
    setFormState((prev) => ({
      ...prev,
      staticOption: value,
      userIdList: '',
      emailList: '',
    }));
  };

  const handleDynamicOptionChange = (value) => {
    // Reset sub-fields when dynamic option changes
    setFormState((prev) => ({
      ...prev,
      dynamicOption: value,
      // clear program-related selections
      programCourseList: [],
      programPlanList: [],
      programSubplanList: [],
      // clear cohort selections
      selectedCohorts: [],
      // clear date range
      dateRange: '',
      startDate: null,
      endDate: null,
    }));
  };
  const handleDeleteStudentgroup = async () => {
    try {
      setIsLoading(true);
      await deleteStudentGroup(rowData?.id);

      // If we reach here, the API call was successful (no error thrown)
      toast.success('Student group deleted successfully');
      handleClose();
      onRefresh();
      // You might also want to refresh the parent component's data here
      // onRefresh?.(); // if you pass a refresh callback from parent
    } catch (error) {
      console.error('Error deleting student group:', error);

      // AWS Amplify API puts the response data in error.response.data
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Something went wrong while deleting the student group.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    if (!formState.groupName.trim()) return false;
    if (!formState.groupType) return false;

    if (formState.groupType === 'static') {
      if (!formState.staticOption) return false;
      if (formState.staticOption === 'userIds') {
        return formState.userIdList.trim().length > 0;
      }
      if (formState.staticOption === 'emails') {
        return formState.emailList.trim().length > 0;
      }
      return false;
    }

    if (formState.groupType === 'dynamic') {
      if (!formState.dynamicOption) return false;

      switch (formState.dynamicOption) {
        case 'program':
          return (
            (Array.isArray(formState.programCourseList) &&
              formState.programCourseList.length > 0) ||
            (Array.isArray(formState.programPlanList) &&
              formState.programPlanList.length > 0) ||
            (Array.isArray(formState.programSubplanList) &&
              formState.programSubplanList.length > 0)
          );
        case 'cohort':
        case 'almaxCohort':
          return (
            Array.isArray(formState.selectedCohorts) &&
            formState.selectedCohorts.length > 0
          );
        case 'daterange':
          return !!(formState.startDate && formState.endDate);
        default:
          return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare payload same as create
      const payload = {
        groupName: formState.groupName,
        groupType: formState.groupType,
        ...(formState.groupType === 'static' && {
          params: {
            type:
              formState.staticOption === 'userIds'
                ? 'userId'
                : formState.staticOption === 'emails'
                ? 'emailId'
                : formState.staticOption,
            ...(formState.staticOption === 'userIds' && {
              userId: formState.userIdList
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean),
            }),
            ...(formState.staticOption === 'emails' && {
              emailId: formState.emailList
                .split(',')
                .map((email) => email.trim())
                .filter(Boolean),
            }),
          },
        }),
        ...(formState.groupType === 'dynamic' && {
          params: (() => {
            const opt = formState.dynamicOption;
            if (opt === 'program') {
              return {
                type: 'program',
                program: {
                  course: formState.programCourseList,
                  subplan: formState.programSubplanList,
                  plan: formState.programPlanList,
                },
              };
            }
            if (opt === 'cohort') {
              return { type: 'cohort', cohort: formState.selectedCohorts };
            }
            if (opt === 'almaxCohort') {
              return {
                type: 'almaxCohort',
                almaxCohort: formState.selectedCohorts,
              };
            }
            if (opt === 'daterange') {
              return {
                type: 'enrolmentDate',
                enrolmentDate: {
                  startDate: formState.startDate,
                  endDate: formState.endDate,
                },
              };
            }
            return {};
          })(),
        }),
      };

      // Call updateStudentGroup API with id and payload
      await updateStudentGroup(payload, rowData?.id, );
      toast.success('Student group updated successfully');
      handleClose();
      onRefresh();
    } catch (error) {
      console.error('Error updating student group:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Something went wrong while updating the student group.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CommonModal
      isOpen={openEditModal}
      setIsOpen={handleClose}
      removePadding
      classStyle='w-[800px] max-w-[90vw] h-auto rounded-md overflow-y-auto'
    >
      <div className='p-8' style={{ width: '700px' }}>
        <h2 className='text-2xl font-semibold mb-8 text-gray-800'>
          Edit Group
        </h2>

        {/* Fixed Container for consistent dimensions */}
        <Box sx={{ minHeight: '300px' }}>
          <Grid container spacing={4}>
            {/* Row 1: Group Name and Group Type - Always Visible */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    label='Group Name'
                    name='groupName'
                    variant='outlined'
                    fullWidth
                    type='text'
                    disabled={true}
                    value={formState.groupName}
                    onChange={(e) =>
                      handleInputChange('groupName', e.target.value)
                    }
                    placeholder='Enter group name'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel>Group Type</InputLabel>
                    <Select
                      value={formState.groupType}
                      onChange={(e) => handleGroupTypeChange(e.target.value)}
                      label='Group Type'
                      sx={{
                        height: '56px',
                        '& .MuiSelect-select': {
                          height: '56px',
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      }}
                      disabled={true}
                    >
                      <MenuItem value='static'>Static</MenuItem>
                      <MenuItem value='dynamic'>Dynamic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Group Description'
                name='groupDescription'
                variant='outlined'
                fullWidth
                multiline
                rows={2}
                value={formState.groupDescription}
                onChange={(e) =>
                  handleInputChange('groupDescription', e.target.value)
                }
                placeholder='Enter group description (optional)'
                helperText='Provide a brief description of the group'
              />
            </Grid>

            {/* Row 2: Static Group Options - Only show when static is selected */}
            {formState.groupType === 'static' && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl variant='outlined' fullWidth>
                      <InputLabel>Choose Input Type</InputLabel>
                      <Select
                        value={formState.staticOption}
                        onChange={(e) =>
                          handleStaticOptionChange(e.target.value)
                        }
                        label='Choose Input Type'
                        sx={{
                          height: '56px',
                          '& .MuiSelect-select': {
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        }}
                        disabled={true}
                      >
                        <MenuItem value='userIds'>User ID List</MenuItem>
                        <MenuItem value='emails'>Email List</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    {formState.staticOption === 'userIds' && (
                      <TextField
                        label='User ID List (comma separated)'
                        name='userIdList'
                        variant='outlined'
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.userIdList}
                        onChange={(e) =>
                          handleInputChange('userIdList', e.target.value)
                        }
                        placeholder='user1, user2, user3'
                        helperText='Enter user IDs separated by commas'
                      />
                    )}
                    {formState.staticOption === 'emails' && (
                      <TextField
                        label='Email List (comma separated)'
                        name='emailList'
                        variant='outlined'
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.emailList}
                        onChange={(e) =>
                          handleInputChange('emailList', e.target.value)
                        }
                        placeholder='email1@example.com, email2@example.com'
                        helperText='Enter emails separated by commas'
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Row 3: Dynamic Group Options - Only show when dynamic is selected */}
            {formState.groupType === 'dynamic' && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl variant='outlined' fullWidth>
                      <InputLabel>Dynamic Option</InputLabel>
                      <Select
                        value={formState.dynamicOption}
                        onChange={(e) =>
                          handleDynamicOptionChange(e.target.value)
                        }
                        label='Dynamic Option'
                        sx={{
                          height: '56px',
                          '& .MuiSelect-select': {
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        }}
                        disabled={true}
                      >
                        <MenuItem value='program'>Program</MenuItem>
                        <MenuItem value='cohort'>Cohort</MenuItem>
                        <MenuItem value='almaxCohort'>AlmaX Cohort</MenuItem>
                        <MenuItem value='daterange'>Date Range</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {/* Program Type Selection */}
                    {formState.dynamicOption === 'program' && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Course</InputLabel>
                            <Select
                              multiple
                              value={formState.programCourseList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programCourseList',
                                  e.target.value
                                )
                              }
                              label='Course'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                minHeight: '56px',
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.course.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programCourseList.indexOf(val) >
                                      -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Select one or more courses
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Plan</InputLabel>
                            <Select
                              multiple
                              value={formState.programPlanList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programPlanList',
                                  e.target.value
                                )
                              }
                              label='Plan'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={
                                        value.charAt(0).toUpperCase() +
                                        value.slice(1)
                                      }
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                minHeight: '56px',
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.plan.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programPlanList.indexOf(val) >
                                      -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Select plan(s)</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Subplan</InputLabel>
                            <Select
                              multiple
                              value={formState.programSubplanList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programSubplanList',
                                  e.target.value
                                )
                              }
                              label='Subplan'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={
                                        value.charAt(0).toUpperCase() +
                                        value.slice(1)
                                      }
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                minHeight: '56px',
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.subplan.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programSubplanList.indexOf(
                                        val
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Select subplan(s)</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    )}

                    {/* Cohort Selection */}
                    {(formState.dynamicOption === 'cohort' ||
                      formState.dynamicOption === 'almaxCohort') && (
                      <FormControl variant='outlined' fullWidth>
                        <InputLabel>
                          {formState.dynamicOption === 'cohort'
                            ? 'Select Cohort'
                            : 'Select Alma Cohort'}
                        </InputLabel>
                        <Select
                          multiple
                          value={formState.selectedCohorts}
                          onChange={(e) =>
                            handleInputChange('selectedCohorts', e.target.value)
                          }
                          label={
                            formState.dynamicOption === 'cohort'
                              ? 'Select Cohort'
                              : 'Select Alma Cohort'
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {selected.map((id) => {
                                const list =
                                  formState.dynamicOption === 'cohort'
                                    ? cohortList
                                    : almaxCohortList;
                                const co = list.find((c) => c.id === id);
                                return (
                                  <Chip
                                    key={id}
                                    label={co?.name || id}
                                    size='small'
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: { style: { maxHeight: 240 } },
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                          }}
                        >
                          {(formState.dynamicOption === 'cohort'
                            ? cohortList
                            : almaxCohortList
                          ).map((cohort) => (
                            <MenuItem key={cohort.id} value={cohort.id} dense>
                              <Checkbox
                                size='small'
                                checked={
                                  formState.selectedCohorts.indexOf(cohort.id) >
                                  -1
                                }
                              />
                              <ListItemText
                                primaryTypographyProps={{ variant: 'body2' }}
                                primary={cohort.name}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          You can pick multiple cohorts
                        </FormHelperText>
                      </FormControl>
                    )}

                    {/* Date Range Picker - Single Input */}
                    {formState.dynamicOption === 'daterange' && (
                      <Box sx={{ width: '100%', position: 'relative' }}>
                        <FormHelperText sx={{ mb: 1 }}>
                          Select enrolment date range
                        </FormHelperText>
                        <DatePicker
                          selectsRange={true}
                          startDate={formState.startDate}
                          endDate={formState.endDate}
                          onChange={handleDateRangeChange}
                          placeholderText='Select date range'
                          isClearable={true}
                          showPopperArrow={false}
                          popperClassName='date-picker-popper'
                          dateFormat='MMM dd, yyyy'
                          popperProps={{
                            positionFixed: true,
                            strategy: 'fixed',
                          }}
                          className='border px-3 py-3 rounded w-full z-50'
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>

        {formState.groupType === 'static' && formState.staticOption && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <p
              style={{ color: '#dc2626', fontSize: '14px', fontWeight: '500' }}
            >
              Note: You can only add new{' '}
              {formState.staticOption === 'userIds' ? 'User IDs' : 'emails'}.
              Existing data cannot be edited through this form.
            </p>
          </Box>
        )}

        {/* Action buttons - Always at the same position */}

        <Box
          sx={{
            mt: 3,
            pt: 3,
            // borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between', // spread left and right sections
            alignItems: 'center',
          }}
        >
          {/* Left-aligned button */}
          <div>
            <button
              className={`px-8 py-3 border rounded-lg font-medium min-w-[120px] flex items-center justify-center transition-colors ${
                isLoading
                  ? 'bg-red-400 text-white cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
              onClick={handleDeleteStudentgroup}
              disabled={isLoading} // disable when loading
            >
              {isLoading ? (
                <>
                  <CircularProgress
                    size={18}
                    style={{ color: 'white', marginRight: '8px' }}
                  />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>

          {/* Right-aligned buttons */}
          <div className='flex gap-4'>
            <button
              className='px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium min-w-[120px]'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className={`px-8 py-3 rounded-lg font-medium transition-colors min-w-[140px] flex items-center justify-center ${
                validateForm() && !isLoading
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress
                    size={20}
                    style={{ color: 'white', marginRight: '8px' }}
                  />
                  Updating...
                </>
              ) : (
                'Update Group'
              )}
            </button>
          </div>
        </Box>
      </div>
    </CommonModal>
  );
};

export default StudentGroupEditModal;
